import { useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Save, User as UserIcon, Mail } from "lucide-react";
import { fmtDateTime } from "../lib/format";

export default function Profile() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/auth/profile", { name });
      toast.success("Profile updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader title="Profile" subtitle="Your admin account details" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm p-6 text-center">
          <div className="h-20 w-20 rounded-sm bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-display text-3xl font-bold mx-auto mb-4">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </div>
          <div className="font-display text-lg font-bold text-gray-900">{user?.name}</div>
          <div className="text-xs text-gray-500 mt-1">{user?.email}</div>
          <div className="mt-3 inline-flex items-center text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
            {user?.role || "admin"}
          </div>
          <div className="text-[11px] text-gray-500 mt-6">
            <div className="flex items-center gap-1.5 justify-center"><UserIcon size={12} /> ID: <span className="font-mono">{user?.id?.slice(0, 8)}</span></div>
            {user?.created_at && <div className="mt-1">Joined {fmtDateTime(user.created_at)}</div>}
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-6 lg:col-span-2" data-testid="profile-form">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-4 border-b border-gray-100 pb-3">Edit profile</div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Full name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="profile-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Email</label>
              <div className="flex items-center gap-2 h-10 px-3 border border-gray-200 rounded-sm text-sm bg-gray-50 text-gray-600">
                <Mail size={14} /> {user?.email}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">Email cannot be changed.</div>
            </div>
          </div>
          <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
            <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="profile-save">
              <Save size={14} /> {loading ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
